import React, { useEffect, useState, useCallback } from "react";
import {
  Typography, Box, Paper, FormControl, InputLabel, Select, MenuItem, Button,
  Dialog, DialogTitle, DialogContent, IconButton, Snackbar,
  DialogActions, List, ListItem, ListItemText,
  Checkbox, ListItemIcon, ListItemButton, CircularProgress // CircularProgress hinzugefügt für Ladeanzeige
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import {
  fetchCustomers,
  fetchPlayersByCustomer,
  deletePlayer,
  fetchAllContent,
  updateAllowedContentForPlayer,
  fetchAllowedContent,
  Content
} from "../api/api";
import Feeds from "./Feeds";
import UserManagement from "../components/Admin/UserManagement";
import CustomerManagement from "../components/Admin/CustomerManagement";
import { useTranslation } from "react-i18next";

type Player = {
  sn: string;
  location: string;
};

const AdminPanel: React.FC = () => {
  const { t } = useTranslation();

  const [customers, setCustomers] = useState<{ id: string; name: string }[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<string | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [selectedPlayer, setSelectedPlayer] = useState<string | null>(null);
  const [showFeedConfig, setShowFeedConfig] = useState<string | null>(null);
  const [snackbarMsg, setSnackbarMsg] = useState<string | null>(null);

  // States for player deletion confirmation dialog
  const [confirmDeletePlayerDialogOpen, setConfirmDeletePlayerDialogOpen] = useState(false);
  const [playerToDeleteSN, setPlayerToDeleteSN] = useState<string | null>(null);

  // NEW: States for content assignment dialog
  const [contentAssignmentDialogOpen, setContentAssignmentDialogOpen] = useState(false);
  const [selectedPlayerForContentAssignment, setSelectedPlayerForContentAssignment] = useState<string | null>(null);
  const [allAvailableContent, setAllAvailableContent] = useState<Content[]>([]);
  const [assignedContentNames, setAssignedContentNames] = useState<string[]>([]);
  const [loadingContent, setLoadingContent] = useState(false);

  // Function to reload customer list (for CustomerManagement)
  const loadCustomers = useCallback(() => {
    fetchCustomers().then(setCustomers);
  }, []);

  // Function to reload players for the currently selected customer
  const loadPlayersForSelectedCustomer = useCallback(() => {
    if (selectedCustomer) {
      fetchPlayersByCustomer(selectedCustomer)
        .then(setPlayers)
        .catch(error => {
          console.error("Error loading players for customer:", error);
          setSnackbarMsg(t("playerManagement.errorLoadingPlayers"));
        });
    } else {
      setPlayers([]);
    }
  }, [selectedCustomer, t]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  useEffect(() => {
    // When the selected customer changes, reload players for this customer
    loadPlayersForSelectedCustomer();
    setSelectedPlayer(null); // Reset player selection on customer change
  }, [selectedCustomer, loadPlayersForSelectedCustomer]);

  // Handler for deleting a player
  const handleDeletePlayerClick = (sn: string) => {
    setPlayerToDeleteSN(sn);
    setConfirmDeletePlayerDialogOpen(true);
  };

  const handleConfirmDeletePlayer = async () => {
    if (playerToDeleteSN && selectedCustomer) {
      try {
        await deletePlayer(selectedCustomer, playerToDeleteSN);
        setSnackbarMsg(t("playerManagement.playerDeleted"));
        loadPlayersForSelectedCustomer(); // Reload player list
        // If the deleted player was the currently selected one, reset selection
        if (selectedPlayer === playerToDeleteSN) {
          setSelectedPlayer(null);
        }
      } catch (error: any) {
        if (error && error.data && error.data.error === "player_assigned_to_users") {
          setSnackbarMsg(t("playerManagement.playerAssignedError"));
        } else {
          setSnackbarMsg(t("common.deletePlayerError"));
          console.error("Error deleting player:", error);
        }
      }
    }
    setConfirmDeletePlayerDialogOpen(false);
    setPlayerToDeleteSN(null);
  };

  const handleCancelDeletePlayer = () => {
    setConfirmDeletePlayerDialogOpen(false);
    setPlayerToDeleteSN(null);
  };

  // NEW: Handler to open content assignment dialog
  const handleOpenContentAssignmentDialog = async (playerSN: string) => {
    setSelectedPlayerForContentAssignment(playerSN);
    setLoadingContent(true);
    try {
      // Fetch all available content
      const allContent = await fetchAllContent();
      setAllAvailableContent(allContent);

      // Fetch content currently assigned to this player
      const assigned = await fetchAllowedContent(playerSN);
      setAssignedContentNames(assigned.map((c: any) => c.content_name)); // Assuming fetchAllowedContent returns objects with content_name

      setContentAssignmentDialogOpen(true);
    } catch (error) {
      console.error("Error loading content for assignment:", error);
      setSnackbarMsg(t("contentManagement.errorLoadingContent"));
    } finally {
      setLoadingContent(false);
    }
  };

  // NEW: Handler for direct selection list content change
  const handleToggleContentAssignment = (contentName: string) => {
    setAssignedContentNames((prevSelected) => {
      if (prevSelected.includes(contentName)) {
        return prevSelected.filter((name) => name !== contentName);
      } else {
        return [...prevSelected, contentName];
      }
    });
  };

  // NEW: Handler to save content assignments
  const handleSaveContentAssignment = async () => {
    if (!selectedPlayerForContentAssignment) return;

    try {
      await updateAllowedContentForPlayer(selectedPlayerForContentAssignment, assignedContentNames);
      setSnackbarMsg(t("contentManagement.contentAssignedSuccess"));
      setContentAssignmentDialogOpen(false);
    } catch (error) {
      console.error("Error saving content assignments:", error);
      setSnackbarMsg(t("contentManagement.errorSavingContent"));
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 4 }}>
        {t("adminPanel.title")}
      </Typography>

      <CustomerManagement onCustomerChange={loadCustomers} />

      <Paper sx={{ p: 1.5, mb: 2 }}>
        {/* Customer Selection */}
        <FormControl fullWidth sx={{ mb: 1.5 }}>
          <InputLabel id="customer-select-label">{t("adminPanel.selectCustomer")}</InputLabel>
          <Select
            labelId="customer-select-label"
            value={selectedCustomer || ""}
            label={t("adminPanel.selectCustomer")}
            onChange={(e) => setSelectedCustomer(e.target.value as string)}
          >
            {customers.map((customer) => (
              <MenuItem key={customer.id} value={customer.id}>
                {customer.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* USER MANAGEMENT: Only if customer selected */}
        {selectedCustomer && (
          <UserManagement
            customerId={selectedCustomer}
            onPlayerCreated={loadPlayersForSelectedCustomer}
          />
        )}

        {/* Player List and Selection */}
        {selectedCustomer && (
          <Paper sx={{ mt: 1.5, p: 1.5 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Typography variant="h6">{t("playerManagement.playersHeading")}</Typography>
            </Box>
            <List>
              {players.length === 0 ? (
                <ListItem>
                  <ListItemText primary={t("playerManagement.noPlayersAvailable")} />
                </ListItem>
              ) : (
                players.map((player) => (
                  <ListItem
                    key={player.sn}
                    secondaryAction={
                      <Box>
                        <IconButton
                          edge="end"
                          aria-label="assign-content"
                          onClick={() => handleOpenContentAssignmentDialog(player.sn)}
                          title={t("contentManagement.assignContentTitle")}
                          sx={{ mr: 1 }}
                        >
                          <ContentPasteIcon />
                        </IconButton>
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => handleDeletePlayerClick(player.sn)}
                          title={t("common.delete")}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemText primary={`${player.sn} (${player.location})`} />
                  </ListItem>
                ))
              )}
            </List>

            {/* Player Selection Dropdown (optional, can also be selected directly from the list) */}
            <FormControl fullWidth sx={{ mb: 1.5, mt: 1.5 }}>
              <InputLabel id="player-select-label">{t("adminPanel.selectPlayer")}</InputLabel>
              <Select
                labelId="player-select-label"
                value={selectedPlayer || ""}
                label={t("adminPanel.selectPlayer")}
                onChange={(e) => setSelectedPlayer(e.target.value as string)}
              >
                {players.map((player) => (
                  <MenuItem key={player.sn} value={player.sn}>
                    {player.sn} ({player.location})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Open Feed Configuration Button */}
            {selectedPlayer && (
              <Box sx={{ mt: 1.5 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setShowFeedConfig(selectedPlayer)}
                >
                  {t("adminPanel.openFeedConfig")}
                </Button>
              </Box>
            )}
          </Paper>
        )}
      </Paper>

      {/* MODAL FEED CONFIGURATION WINDOW */}
      <Dialog
        open={!!showFeedConfig}
        onClose={() => setShowFeedConfig(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { minHeight: "70vh" } }}
      >
        <DialogTitle sx={{ m: 0, p: 2, pr: 6 }}>
          {t("adminPanel.feedConfigForPlayer", { playerSn: showFeedConfig })}
          {selectedCustomer && (
            t("adminPanel.customerInfo", { customerName: customers.find(c => c.id === selectedCustomer)?.name })
          )}
          <IconButton
            aria-label="close"
            onClick={() => setShowFeedConfig(null)}
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {showFeedConfig && <Feeds adminPlayer={showFeedConfig} />}
        </DialogContent>
      </Dialog>

      {/* CONFIRMATION DIALOG for deleting players */}
      <Dialog
        open={confirmDeletePlayerDialogOpen}
        onClose={handleCancelDeletePlayer}
        aria-labelledby="confirm-delete-player-title"
        aria-describedby="confirm-delete-player-description"
      >
        <DialogTitle id="confirm-delete-player-title">{t("playerManagement.confirmDeleteTitle")}</DialogTitle>
        <DialogContent>
          <Typography id="confirm-delete-player-description">
            {t("playerManagement.confirmDeleteMessage", { playerSN: playerToDeleteSN })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDeletePlayer} color="primary">{t("common.cancel")}</Button>
          <Button onClick={handleConfirmDeletePlayer} color="error" autoFocus>{t("common.delete")}</Button>
        </DialogActions>
      </Dialog>

      {/* NEW: Content Assignment Dialog */}
      <Dialog open={contentAssignmentDialogOpen} onClose={() => setContentAssignmentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("contentManagement.assignContentDialogTitle", { playerSN: selectedPlayerForContentAssignment })}</DialogTitle>
        <DialogContent>
          {loadingContent ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress size={24} sx={{ mr: 2 }} /> {/* Ladeanzeige */}
              <Typography>{t("contentManagement.loadingContent")}</Typography>
            </Box>
          ) : (
            <List sx={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}> {/* Direkte Selektionsliste mit Scrollbalken */}
              {allAvailableContent.length === 0 ? (
                <ListItem>
                  <ListItemText primary={t("contentManagement.noContentAvailable")} />
                </ListItem>
              ) : (
                allAvailableContent.map((content) => (
                  <ListItemButton
                    key={content.file_name}
                    onClick={() => handleToggleContentAssignment(content.content_name)}
                    dense // Macht die Listeneinträge kompakter
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={assignedContentNames.indexOf(content.content_name) > -1}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText primary={`${content.content_name} (${content.file_name})`} />
                  </ListItemButton>
                ))
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContentAssignmentDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button onClick={handleSaveContentAssignment} disabled={loadingContent}>
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbarMsg}
        autoHideDuration={3000}
        onClose={() => setSnackbarMsg(null)}
        message={snackbarMsg}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      />
    </Box>
  );
};

export default AdminPanel;
